import React, { useState, useEffect } from "react";
import { auth, sendPasswordResetEmail } from "shared/utils/firebase";
import styles from "./ForgotPasswordModal.module.css";

function ForgotPasswordModal({ isOpen, onClose, initialEmail = "" }) {
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setEmail(initialEmail || "");
      setErrorMsg("");
      setIsSuccess(false);
      setLoading(false);
    }
  }, [isOpen, initialEmail]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanEmail = (email || "").trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes("@")) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    setErrorMsg("");

    try {
      const actionCodeSettings = {
        url: `${window.location.origin}/login`,
        handleCodeInApp: false,
      };
      await sendPasswordResetEmail(auth, cleanEmail, actionCodeSettings);
      setIsSuccess(true);
    } catch (err) {
      console.error("[ForgotPassword] Error sending reset link:", err);
      let message = "Failed to send password reset email. Please try again.";
      if (err.code === "auth/user-not-found") {
        message = "No account found registered with this email address.";
      } else if (err.code === "auth/invalid-email") {
        message = "Please enter a valid email address format.";
      } else if (err.code === "auth/too-many-requests" || err.message?.includes("EXCEED_LIMIT")) {
        message = "Too many reset attempts for this email address. Firebase has applied a temporary security cooldown (15–30 min). Please check your inbox or spam for earlier links before trying again.";
      } else if (err.message) {
        message = err.message;
      }
      setErrorMsg(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
          ✕
        </button>

        {!isSuccess ? (
          <>
            <div className={styles.iconWrapper}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </div>

            <h3 className={styles.title}>Reset Your Password</h3>
            <p className={styles.desc}>
              Enter your registered SiliconLogix account email and we'll dispatch a secure recovery link.
            </p>

            {errorMsg && <div className={styles.errorBanner}>{errorMsg}</div>}

            <form onSubmit={handleSubmit}>
              <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="reset-email">Email Address</label>
                <input
                  id="reset-email"
                  type="email"
                  required
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={styles.input}
                  autoFocus
                />
              </div>

              <button type="submit" className={styles.submitBtn} disabled={loading}>
                {loading ? "Sending Recovery Link..." : "Send Password Reset Link"}
              </button>
            </form>
          </>
        ) : (
          <div className={styles.successBox}>
            <div className={styles.successIcon}>
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h3 className={styles.title}>Recovery Link Dispatched</h3>
            <p className={styles.desc}>
              We've dispatched a password reset link to <strong>{email}</strong> from <strong>admin@siliconlogix.in</strong>. Please check your inbox (and Spam/Junk folder) and follow the instructions to set a new password.
            </p>
            <button className={styles.submitBtn} onClick={onClose}>
              Back to Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ForgotPasswordModal;
